import React, { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Input,
  SimpleGrid,
  useToast,
} from "@chakra-ui/react";
import EmployeeDataService from "../../../services/Employee";
import { getUserAccess } from "../../../utils/authUtils";
import { HrmsPageShell } from "../../../components/Hrms/HrmsPageShell";
import { HrmsCard } from "../../../components/Hrms/HrmsCard";

interface AttendanceRuleForm {
  shiftStartTime: string;
  shiftEndTime: string;
  gracePeriodMinutes: number;
  halfDayAfterMinutesLate: number;
  minFullDayHours: number;
  minHalfDayHours: number;
  earlyExitThresholdMinutes: number;
  overtimeEnabled: boolean;
  overtimeThresholdMinutes: number;
}

const emptyForm: AttendanceRuleForm = {
  shiftStartTime: "09:30",
  shiftEndTime: "18:30",
  gracePeriodMinutes: 10,
  halfDayAfterMinutesLate: 240,
  minFullDayHours: 8,
  minHalfDayHours: 4,
  earlyExitThresholdMinutes: 15,
  overtimeEnabled: true,
  overtimeThresholdMinutes: 30,
};

export const AttendanceRuleMaster = () => {
  const token = localStorage.getItem("logged_in_user_token");
  const userEmail = localStorage.getItem("logged_in_email") || "";
  const toast = useToast();
  const { canManageConfig } = getUserAccess();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState<AttendanceRuleForm>(emptyForm);

  const fetchRule = async () => {
    setIsLoading(true);
    try {
      const res = await EmployeeDataService.getActiveAttendanceRule(token);
      if (res.data.status === "success" && res.data.data) {
        const rule = res.data.data;
        setForm({
          shiftStartTime: rule.shiftStartTime,
          shiftEndTime: rule.shiftEndTime,
          gracePeriodMinutes: rule.gracePeriodMinutes,
          halfDayAfterMinutesLate: rule.halfDayAfterMinutesLate,
          minFullDayHours: rule.minFullDayHours,
          minHalfDayHours: rule.minHalfDayHours,
          earlyExitThresholdMinutes: rule.earlyExitThresholdMinutes,
          overtimeEnabled: rule.overtimeEnabled,
          overtimeThresholdMinutes: rule.overtimeThresholdMinutes,
        });
      }
    } catch (e: any) {
      console.error("Error fetching attendance rule:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const res = await EmployeeDataService.updateAttendanceRule(
        token,
        form,
        userEmail,
      );
      if (res.data.status === "success") {
        toast({
          position: "top",
          title: "Attendance rule updated",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      }
    } catch (e: any) {
      toast({
        position: "top",
        title: "Error",
        description: e.response?.data?.message || "Failed to update rule.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    fetchRule();
  }, []);

  return (
    <HrmsPageShell
      title="Attendance Rule Master"
      description="Shift timings and thresholds used to mark late arrivals, early exits, half days and overtime"
      sectionKey="configuration"
      noPermission={!canManageConfig}
      isLoading={canManageConfig && isLoading}
    >
      <HrmsCard maxW="960px" title="Shift rules">
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <FormControl>
            <FormLabel>Shift Start Time</FormLabel>
            <Input
              type="time"
              value={form.shiftStartTime}
              onChange={(e) =>
                setForm({ ...form, shiftStartTime: e.target.value })
              }
            />
          </FormControl>

          <FormControl>
            <FormLabel>Shift End Time</FormLabel>
            <Input
              type="time"
              value={form.shiftEndTime}
              onChange={(e) =>
                setForm({ ...form, shiftEndTime: e.target.value })
              }
            />
          </FormControl>

          <FormControl>
            <FormLabel>Grace Period (minutes)</FormLabel>
            <Input
              type="number"
              value={form.gracePeriodMinutes}
              onChange={(e) =>
                setForm({
                  ...form,
                  gracePeriodMinutes: Number(e.target.value),
                })
              }
            />
          </FormControl>

          <FormControl>
            <FormLabel>Mark Half Day if Late By (minutes)</FormLabel>
            <Input
              type="number"
              value={form.halfDayAfterMinutesLate}
              onChange={(e) =>
                setForm({
                  ...form,
                  halfDayAfterMinutesLate: Number(e.target.value),
                })
              }
            />
          </FormControl>

          <FormControl>
            <FormLabel>Minimum Full Day Hours</FormLabel>
            <Input
              type="number"
              value={form.minFullDayHours}
              onChange={(e) =>
                setForm({
                  ...form,
                  minFullDayHours: Number(e.target.value),
                })
              }
            />
          </FormControl>

          <FormControl>
            <FormLabel>Minimum Half Day Hours</FormLabel>
            <Input
              type="number"
              value={form.minHalfDayHours}
              onChange={(e) =>
                setForm({
                  ...form,
                  minHalfDayHours: Number(e.target.value),
                })
              }
            />
          </FormControl>

          <FormControl>
            <FormLabel>Early Exit Threshold (minutes)</FormLabel>
            <Input
              type="number"
              value={form.earlyExitThresholdMinutes}
              onChange={(e) =>
                setForm({
                  ...form,
                  earlyExitThresholdMinutes: Number(e.target.value),
                })
              }
            />
          </FormControl>

          <FormControl>
            <FormLabel>Overtime Threshold (minutes worked beyond shift hours)</FormLabel>
            <Input
              type="number"
              value={form.overtimeThresholdMinutes}
              onChange={(e) =>
                setForm({
                  ...form,
                  overtimeThresholdMinutes: Number(e.target.value),
                })
              }
              isDisabled={!form.overtimeEnabled}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Overtime Calculation</FormLabel>
            <Checkbox
              isChecked={form.overtimeEnabled}
              onChange={(e) =>
                setForm({ ...form, overtimeEnabled: e.target.checked })
              }
            >
              Enabled
            </Checkbox>
          </FormControl>
        </SimpleGrid>

        <Flex justify="flex-end" mt={6}>
          <Button
            bg="#327171"
            color="white"
            _hover={{ bg: "#285e5e" }}
            onClick={handleSave}
            isLoading={isSaving}
          >
            Save Rule
          </Button>
        </Flex>
      </HrmsCard>
    </HrmsPageShell>
  );
};
